<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useMagazineArticlesStore } from '~/stores/magazine/Articles'
import { usePostsStore } from '~/stores/forum/Posts'
import { useProductsStore } from '~/stores/product/Products'
import { useProductReviewsStore } from '~/stores/product/Reviews'
import ArticlePreview from '~/components/Magazine/ArticlePreview.vue'
import Review from '~/components/Product/Review.vue'
import ProductPreview from '~/components/Product/ProductPreview.vue'
import type { Profile } from '~/types/profile'
import AddUsernameModal from '~/components/Home/AddUsernameModal.vue'
import AddAvatarModal from '~/components/Home/AddAvatarModal.vue'
import AddBioModal from '~/components/Home/AddBioModal.vue'
import Toast, { type ToastNotification } from '~/components/Toast.vue'
import Post from '~/components/Discussions/Post.vue'
import { markAsActive } from '~/helpers/profiles'
import Content from '~/components/Common/Content.vue'
import { useState } from '#app'

// Define this page as SSR
definePageMeta({
	ssr: true,
})
useHead({
	title: 'Index',
})
useSeoMeta({
	ogSiteName: 'Health Nutrition',
	title: 'Health Community',
	description:
		'Your trusted source for health and wellness information. Join our community and discover the latest articles, tips, and expert insights.',
	ogImage: 'https://healthnutrition.com/icon.png',
	//twitterCard: 'summary_large_image',
})

const route = useRoute()
const isAuthed = inject('isAuthed') as Ref<boolean>
const profile = inject('profile') as Ref<Profile>
const articlesStore = useMagazineArticlesStore(0)
const postStore = usePostsStore(0)
const reviewsStore = useProductReviewsStore(0)
const productsStore = useProductsStore()
const isLoadingArticles = ref(false)
const isLoadingDiscussions = ref(false)
const isLoadingReviews = ref(false)
const isLoadingProducts = ref(false)
const addUsernameModal = ref(false)
const addAvatarModal = ref(false)
const addBioModal = ref(false)
const isLoadingMore = ref(false)
const feed = useState<any[]>('feed', () => [])
const noMoreContent = ref(false) // Added flag to track when all content has been loaded

const feedMatrix = ref({
	page: {
		page: 1,
	},
	posts: {
		perPage: 12,
	},
	articles: {
		perPage: 4,
	},
	reviews: {
		perPage: 4,
	},
	products: {
		perPage: 4,
	},
	order: [
		'post',
		'article',
		'post',
		'review',
		'post',
		'product',
		'post',
		'article',
		'post',
		'review',
		'post',
		'article',
		'post',
		'product',
		'post',
		'review',
		'post',
		'article',
		'post',
		'product',
		'post',
		'review',
		'post',
		'product',
	],
})

const toast = ref<ToastNotification>({
	show: false,
})

async function fetchArticles() {
	if (isLoadingArticles.value) {
		return
	}
	isLoadingArticles.value = true
	try {
		const articles = await articlesStore().list({
			sort: 'created_at.desc',
			fields: ['id', 'title', 'handle', 'votes', 'author_id', 'category_id', 'comment_count', 'created_at'],
			limit: feedMatrix.value.articles.perPage,
			offset: feedMatrix.value.page.page > 1 ? feedMatrix.value.page.page * feedMatrix.value.articles.perPage : 0,
			relations: 'magazine_categories,users,magazine_articles_upvotes,magazine_articles_downvotes',
		})
		return articles
	} catch (error) {
		console.error('Error loading articles', error)
	} finally {
		isLoadingArticles.value = false
	}
}

async function fetchDiscussions() {
	if (isLoadingDiscussions.value) {
		return
	}
	isLoadingDiscussions.value = true
	try {
		const discussions = await postStore().list({
			sort: 'created_at.desc',
			limit: feedMatrix.value.posts.perPage,
			offset: feedMatrix.value.page.page > 1 ? feedMatrix.value.page.page * feedMatrix.value.posts.perPage : 0,
			fields: ['id', 'title', 'handle', 'forum_id', 'user_id', 'reply_count', 'votes', 'created_at'],
			relations: 'users,forums_posts_upvotes,forums_posts_downvotes,forums',
		})
		return discussions
	} catch (error) {
		console.error('Error loading discussions', error)
	} finally {
		isLoadingDiscussions.value = false
	}
}

async function fetchReviews() {
	if (isLoadingReviews.value) {
		return
	}
	isLoadingReviews.value = true
	try {
		const reviews = await reviewsStore().list({
			sort: 'created_at.desc',
			limit: feedMatrix.value.reviews.perPage,
			offset: feedMatrix.value.page.page > 1 ? feedMatrix.value.page.page * feedMatrix.value.reviews.perPage : 0,
			relations: 'product,users,product_reviews_upvotes,product_reviews_downvotes',
		})
		return reviews
	} catch (error) {
		console.error('Error loading reviews', error)
	} finally {
		isLoadingReviews.value = false
	}
}

async function fetchProducts() {
	if (isLoadingProducts.value) {
		return
	}
	isLoadingProducts.value = true
	try {
		const products = await productsStore().list({
			fields: [
				'id',
				'name',
				'handle',
				'review_rating',
				'image_url',
				'category_id',
				'product_categories.id',
				'product_categories.handle',
			],
			sort: 'created_at.desc',
			limit: feedMatrix.value.products.perPage,
			offset: feedMatrix.value.page.page > 1 ? feedMatrix.value.page.page * feedMatrix.value.products.perPage : 0,
			relations: 'product_categories',
		})
		return products
	} catch (error) {
		console.error('Error loading products', error)
	} finally {
		isLoadingProducts.value = false
	}
}

async function fetchAll() {
	const result = await Promise.all([fetchArticles(), fetchDiscussions(), fetchReviews(), fetchProducts()])

	const newItems = []
	for (const t of feedMatrix.value.order) {
		if (t === 'post' && result[1].data.length > 0) {
			newItems.push({ ...result[1].data.shift(), type: 'post' })
		} else if (t === 'article' && result[0].data.length > 0) {
			newItems.push({ ...result[0].data.shift(), type: 'article' })
		} else if (t === 'review' && result[2].data.length > 0) {
			newItems.push({ ...result[2].data.shift(), type: 'review' })
		} else if (t === 'product' && result[3].data.length > 0) {
			newItems.push({ ...result[3].data.shift(), type: 'product' })
		}
	}

	// Check if we received any new items
	if (newItems.length === 0) {
		noMoreContent.value = true
		return false
	}

	// If this is the initial load, set the feed value
	// Otherwise, append new items to existing feed
	if (feedMatrix.value.page.page === 1) {
		feed.value = newItems
	} else {
		feed.value = [...feed.value, ...newItems]
	}

	return true
}

onServerPrefetch(async () => {
	await fetchAll()
})

async function loadMore() {
	feedMatrix.value.page.page++
	const hasNewContent = await fetchAll()

	// If no new content was loaded, we don't need to try loading more
	if (!hasNewContent) {
		console.log('No more content available')
	}
}

function closeModal(modal: 'addUsername' | 'addAvatar' | 'addBio') {
	switch (modal) {
		case 'addUsername':
			addUsernameModal.value = false
			break
		case 'addAvatar':
			addAvatarModal.value = false
			break
		case 'addBio':
			addBioModal.value = false
			break
	}
}

function modalError(modal: 'addUsername' | 'addBio') {
	switch (modal) {
		case 'addUsername':
			addUsernameModal.value = false

			toast.value = {
				show: true,
				timeout: 10,
				title: `Error adding username`,
				message: `There was an error submitting your username, please try again later.`,
				type: 'error',
			}

			break
		case 'addBio':
			addBioModal.value = false

			toast.value = {
				show: true,
				timeout: 10,
				title: `Error adding bio`,
				message: `There was an error submitting your bio, please try again later.`,
				type: 'error',
			}

			break
	}
}

function modalSuccess(modal: 'addUsername' | 'addBio') {
	switch (modal) {
		case 'addUsername':
			addUsernameModal.value = false

			toast.value = {
				show: true,
				timeout: 10,
				title: `Username Added`,
				message: `Your username has been successfully added.`,
				type: 'success',
			}
			break

		case 'addBio':
			addBioModal.value = false

			toast.value = {
				show: true,
				timeout: 10,
				title: `Bio Added`,
				message: `Your bio has been successfully added.`,
				type: 'success',
			}
			break
	}
}

onMounted(async () => {
	if (isAuthed.value) {
		await markAsActive()
	}

	if (route.query?.login === 'success' && profile.value?.id) {
		if (!profile.value?.username) {
			addUsernameModal.value = true
		} else if (!profile.value?.avatar_url) {
			addAvatarModal.value = true
		} else if (!profile.value?.bio) {
			addBioModal.value = true
		}
	}

	if (feed.value.length === 0) {
		fetchAll()
	}

	isLoadingMore.value = false

	window.onscroll = async () => {
		if (isLoadingMore.value || noMoreContent.value) {
			return
		}
		isLoadingMore.value = true
		const bottomOfWindow =
			Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) +
				window.innerHeight >=
			document.documentElement.offsetHeight - 100

		if (bottomOfWindow) {
			await loadMore()
		}
		isLoadingMore.value = false
	}
})

onUnmounted(() => {
	window.onscroll = null
})
</script>

<template>
	<Content>
		<main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" ref="el">
			<Toast :toast="toast" v-if="toast.show" />

			<div class="space-y-8">
				<section>
					<div class="container m-auto">
						<div class="columns-1 lg:columns-2 gap-4 space-y-4">
							<div
								v-for="item in feed"
								:key="item.type + '-' + item.id"
								class="group mb-4 break-inside-avoid">
								<div v-if="item.type === 'article'" class="card">
									<ArticlePreview :article="item" theme="full" showTypeTag />
								</div>

								<div v-else-if="item.type === 'post'">
									<Post
										:forum="item.forums[0]"
										:post="item"
										destination="post"
										theme="full"
										showTypeTag />
								</div>

								<div v-else-if="item.type === 'review'">
									<Review :review="item" :product="item.product?.[0]" theme="full" showTypeTag />
								</div>

								<div v-else-if="item.type === 'product'" class="card">
									<ProductPreview :product="item" theme="full" showTypeTag />
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Loading indicator -->
				<div v-if="isLoadingMore" class="text-center mt-4 mb-8">
					<div
						class="animate-spin rounded-full h-8 w-8 border-2 border-green-600 border-t-transparent justify-self-center" />
				</div>

				<!-- No more content message -->
				<div v-if="noMoreContent" class="text-center mt-4 mb-8">
					<p class="text-gray-500">You have reached the end of the content.</p>
				</div>
			</div>
		</main>

		<AddUsernameModal
			v-if="profile"
			:show="addUsernameModal"
			:user="profile"
			@success="modalSuccess('addUsername')"
			@close="closeModal('addUsername')"
			@errored="modalError('addUsername')" />
		<AddAvatarModal v-if="profile" :show="addAvatarModal" @close="closeModal('addAvatar')" />
		<AddBioModal
			v-if="profile"
			:show="addBioModal"
			:user="profile"
			@close="closeModal('addBio')"
			@success="modalSuccess('addBio')"
			@errored="modalError('addBio')" />
	</Content>
</template>
